import { getTokenIcon, getTokenLogoFallback } from "@/lib/token-assets";
import { cn } from "@/lib/utils";

export type BrandTokenId = "USDM" | "MMK";

const sizeClass = {
	sm: "size-6",
	md: "size-8",
	lg: "size-10",
	xl: "size-12",
} as const;

const LABELS: Record<BrandTokenId, string> = {
	USDM: "Motoko Dollar",
	MMK: "MotokoMaker",
};

/** Official brand mark for USDM / MMK (token PNG + logo fallback, publicUrl-safe). */
export function BrandTokenMark({
	token,
	size = "md",
	className,
}: {
	token: BrandTokenId;
	size?: keyof typeof sizeClass;
	className?: string;
}) {
	const primary = getTokenIcon(token);
	const fallback = getTokenLogoFallback(token);
	const label = LABELS[token];

	if (!primary && !fallback) {
		return (
			<span
				className={cn(
					"brand-token-mark inline-flex items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary",
					sizeClass[size],
					className,
				)}
			>
				{token.slice(0, 3)}
			</span>
		);
	}

	return (
		<span
			className={cn(
				"brand-token-mark inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-background",
				sizeClass[size],
				className,
			)}
		>
			<img
				src={primary ?? fallback}
				alt={label}
				className="size-full object-contain p-0.5"
				decoding="async"
				draggable={false}
				onError={(e) => {
					const el = e.currentTarget;
					if (el.getAttribute("data-fallback") === "1" || !fallback) {
						el.style.display = "none";
						return;
					}
					el.setAttribute("data-fallback", "1");
					el.src = fallback;
				}}
			/>
		</span>
	);
}
